import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

// AMD and NVIDIA GPU identities derived from bkmain.gpu_list on 2026-08-15.

export interface GpuArchitectureEntry {
  readonly vendor: 'amd' | 'nvidia';
  readonly model: string;
  readonly deviceId: string;
  readonly architecture: string;
}

interface WebGlGpuIdentity {
  readonly model: string;
  readonly deviceId: string | null;
}

// prettier-ignore
export const gpuArchitectureCatalog: readonly GpuArchitectureEntry[] = [
  { vendor: 'amd', model: 'AMD Radeon HD 7800 Series', deviceId: '0x6819', architecture: 'gcn-1' },
  { vendor: 'amd', model: 'AMD Radeon RX 580 2048SP', deviceId: '0x6fdf', architecture: 'gcn-4' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Graphics', deviceId: '0x1636', architecture: 'gcn-5' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Graphics', deviceId: '0x1638', architecture: 'gcn-5' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Graphics', deviceId: '0x164c', architecture: 'rdna-2' },
  { vendor: 'amd', model: 'AMD Radeon(TM) R4 Graphics', deviceId: '0x9851', architecture: 'gcn-2' },
  { vendor: 'amd', model: 'AMD Radeon(TM) R4 Graphics', deviceId: '0x98e4', architecture: 'gcn-3' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Vega 3 Graphics', deviceId: '0x15d8', architecture: 'gcn-5' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Vega 3 Graphics', deviceId: '0x15dd', architecture: 'gcn-5' },
  { vendor: 'amd', model: 'AMD Radeon(TM) Vega 8 Graphics', deviceId: '0x15d8', architecture: 'gcn-5' },
  { vendor: 'amd', model: 'Radeon RX 570 Series', deviceId: '0x67df', architecture: 'gcn-4' },
  { vendor: 'amd', model: 'Radeon RX 580 Series', deviceId: '0x67df', architecture: 'gcn-4' },
  { vendor: 'amd', model: 'Radeon RX550/550 Series', deviceId: '0x699f', architecture: 'gcn-4' },
  { vendor: 'amd', model: 'AMD Radeon RX 6600', deviceId: '0x73ff', architecture: 'rdna-2' },
  { vendor: 'amd', model: 'AMD Radeon RX 6750 XT', deviceId: '0x73df', architecture: 'rdna-2' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 1030', deviceId: '0x1d01', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 440', deviceId: '0x0de0', architecture: 'fermi' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 520', deviceId: '0x1040', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 630', deviceId: '0x0f00', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 710', deviceId: '0x128b', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 730', deviceId: '0x0f02', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GT 730', deviceId: '0x1287', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTS 450', deviceId: '0x0dc4', architecture: 'fermi' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1050', deviceId: '0x1c81', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1050 Ti', deviceId: '0x1c82', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1060 3GB', deviceId: '0x1c02', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1060 6GB', deviceId: '0x1c03', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1070', deviceId: '0x1b81', architecture: 'pascal' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1630', deviceId: '0x1f83', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1650', deviceId: '0x1f91', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1650', deviceId: '0x2188', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1650 SUPER', deviceId: '0x2187', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 1660', deviceId: '0x2184', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 550 Ti', deviceId: '0x1244', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 560', deviceId: '0x1201', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 650', deviceId: '0x0fc6', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 660 Ti', deviceId: '0x1183', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 750', deviceId: '0x1381', architecture: 'maxwell' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 750 Ti', deviceId: '0x1380', architecture: 'maxwell' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 760', deviceId: '0x1187', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 880M', deviceId: '0x1198', architecture: 'kepler' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 960', deviceId: '0x1401', architecture: 'maxwell' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce GTX 970', deviceId: '0x13c2', architecture: 'maxwell' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce RTX 2060', deviceId: '0x1f08', architecture: 'turing' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce RTX 3060', deviceId: '0x2487', architecture: 'ampere' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce RTX 3060', deviceId: '0x2504', architecture: 'ampere' },
  { vendor: 'nvidia', model: 'NVIDIA GeForce RTX 4060', deviceId: '0x2882', architecture: 'lovelace' },
];

function identityKey(model: string, deviceId: string | null): string {
  return `${model.toLowerCase()}\u0000${deviceId ?? ''}`;
}

const gpuByIdentity = new Map(
  gpuArchitectureCatalog.map((gpu) => [
    identityKey(gpu.model, gpu.deviceId),
    gpu,
  ]),
);

export function parseWebGlGpuIdentity(
  renderer: string,
): WebGlGpuIdentity | null {
  const details = /^ANGLE\s*\([^,]+,\s*([^,]+)/i.exec(renderer)?.[1].trim();
  if (!details) {
    return null;
  }
  const deviceIdMatch = /\(0x([0-9a-f]+)\)/i.exec(details);
  const model = details
    .replace(/\s+\(0x[0-9a-f]+\).*$/i, '')
    .replace(/\s+Direct3D.*$/i, '')
    .trim();
  if (!model) {
    return null;
  }
  return {
    model,
    deviceId: deviceIdMatch
      ? `0x${Number.parseInt(deviceIdMatch[1], 16).toString(16).padStart(4, '0')}`
      : null,
  };
}

export function gpuArchitectureEntry(
  model: string,
  deviceId: string | null,
): GpuArchitectureEntry | null {
  return gpuByIdentity.get(identityKey(model, deviceId)) ?? null;
}

interface NavigatorWithGpu {
  readonly gpu?: {
    requestAdapter(): Promise<{
      readonly info: {
        readonly vendor: string;
        readonly architecture: string;
        readonly device: string;
        readonly description: string;
        readonly subgroupMinSize: number;
        readonly subgroupMaxSize: number;
        readonly isFallbackAdapter: boolean;
        readonly driver?: string;
        readonly backend?: string;
        readonly type?: string;
        readonly d3dShaderModel?: number | null;
        readonly vkDriverVersion?: number | null;
        readonly powerPreference?: string;
      };
    } | null>;
  };
}

// prettier-ignore
const architecturesByVendor: Readonly<Record<string, readonly string[]>> = {
  amd: ['terascale-2', 'gcn-1', 'gcn-2', 'gcn-3', 'gcn-4', 'gcn-5', 'rdna-1', 'rdna-2', 'rdna-3', 'rdna-4', 'cdna-1'],
  apple: ['metal-3', 'common-1', 'common-2', 'common-3'],
  arm: ['midgard', 'bifrost', 'valhall', 'gen-5'],
  broadcom: ['videocore'],
  google: ['swiftshader'],
  huawei: ['maleoon'],
  'img-tec': ['rogue', 'furian', 'b-series', 'd-series'],
  intel: ['gen-7', 'gen-8', 'gen-9', 'gen-11', 'gen-12lp', 'gen-12hp', 'xe-lpg', 'xe-2lpg', 'xe-2hpg', 'xe-3lpg', 'xe-3lpg-xs'],
  mesa: ['software'],
  microsoft: ['warp'],
  nvidia: ['fermi', 'kepler', 'maxwell', 'pascal', 'turing', 'volta', 'ampere', 'lovelace', 'blackwell'],
  qualcomm: ['adreno-4xx', 'adreno-5xx', 'adreno-6xx', 'adreno-7xx', 'adreno-8xx'],
  samsung: ['rdna-2', 'rdna-3'],
  verisilicon: [],
};

function serializeAdapterInfo(
  info: NonNullable<
    Awaited<ReturnType<NonNullable<NavigatorWithGpu['gpu']>['requestAdapter']>>
  >['info'],
): RawData {
  const rawData: RawData = {
    vendor: info.vendor,
    architecture: info.architecture,
    device: info.device,
    description: info.description,
    subgroupMinSize: info.subgroupMinSize,
    subgroupMaxSize: info.subgroupMaxSize,
    isFallbackAdapter: info.isFallbackAdapter,
  };
  for (const key of [
    'driver',
    'backend',
    'type',
    'd3dShaderModel',
    'vkDriverVersion',
    'powerPreference',
  ] as const) {
    if (key in info) {
      rawData[key] = info[key];
    }
  }
  return rawData;
}

async function collect(): Promise<RawData> {
  const gpu = (navigator as unknown as NavigatorWithGpu).gpu;
  if (!gpu) {
    return { available: false };
  }
  const adapter = await gpu.requestAdapter();
  if (!adapter) {
    throw new Error('WebGPU did not return an adapter');
  }
  return { info: serializeAdapterInfo(adapter.info) };
}

function rawAdapterInfo(rawData: RawData): RawData {
  return rawData.info != null && typeof rawData.info == 'object'
    ? (rawData.info as RawData)
    : {};
}

function validateWebGpuAdapter(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const info = rawAdapterInfo(rawData);
  const architecture = String(info.architecture ?? '');
  const vendor = String(info.vendor ?? '');
  const allowedArchitectures = architecturesByVendor[vendor];
  const renderer = String(
    context.results['webglRenderer']?.rawData.webgl1 ?? '',
  );
  const gpuIdentity = parseWebGlGpuIdentity(renderer);
  const catalogEntry = gpuIdentity
    ? gpuArchitectureEntry(gpuIdentity.model, gpuIdentity.deviceId)
    : null;

  if (!architecture) {
    reasons.push('WebGPU architecture is unavailable');
  } else if (!allowedArchitectures?.includes(architecture)) {
    reasons.push(
      `WebGPU architecture ${architecture} is not allowed for vendor ${vendor || 'unknown'}`,
    );
  }

  if (!catalogEntry) {
    reasons.push(
      'WebGL GPU identity is not present in the architecture catalog',
    );
  } else {
    if (vendor != catalogEntry.vendor) {
      reasons.push(
        `WebGL GPU identity requires WebGPU vendor ${catalogEntry.vendor}`,
      );
    }

    if (architecture && architecture != catalogEntry.architecture) {
      reasons.push(
        `WebGL GPU identity requires WebGPU architecture ${catalogEntry.architecture}`,
      );
    }
  }

  if (info.isFallbackAdapter == true) {
    reasons.push(
      'WebGPU selected a fallback adapter for a physical GPU persona',
    );
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const info = rawAdapterInfo(rawData);
  const identity = [info.architecture, info.device, info.description].find(
    (value) => typeof value == 'string' && value,
  );
  if (identity) {
    return String(identity);
  }
  return rawData.available == false ? 'Unavailable' : 'Available';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateWebGpuAdapter(rawData, context);
}

export const webGpuAdapterTest: TestDefinition = {
  id: 'webgpuAdapter',
  title: 'WebGPU adapter',
  category: 'stableProfile',
  spoofingChromeArgs: ['webgpu-architecture'],
  format,
  run,
  validate,
};
